import { useState } from 'react';
import { BookOpen, PieChart, Ruler, Target, Store, Footprints, RotateCcw } from 'lucide-react';
import type { CityStatistics, Street, Bookshop } from '../types';

interface CityStatsProps {
  stats: CityStatistics | null;
  streets: Street[];
  onClusterClick?: (location: string) => void;
  onStreetClick?: (shop: Bookshop) => void;
}

const MarketDiversityBar = ({ 
  independentPct,
  miniChainPct,
  smallChainPct,
  majorChainPct 
}: {
  independentPct: number;
  miniChainPct: number;
  smallChainPct: number;
  majorChainPct: number;
}) => {
  return (
    <div className="mt-4">
      <div className="h-3 w-full flex mb-2">
        <div 
          style={{ width: `${independentPct}%` }}
          className="bg-blue-400"
        />
        <div 
          style={{ width: `${miniChainPct}%` }}
          className="bg-yellow-400"
        />
        <div 
          style={{ width: `${smallChainPct}%` }}
          className="bg-orange-400"
        />
        <div 
          style={{ width: `${majorChainPct}%` }}
          className="bg-red-400"
        />
      </div>

      <div className="grid grid-cols-4 text-[10px] text-zinc-500">
        <div className="text-left">
          <div><span className="text-blue-400">■</span> {independentPct.toFixed(1)}%</div>
          <div>Ind</div>
        </div>
        <div className="text-left">
          <div><span className="text-yellow-400">■</span> {miniChainPct.toFixed(1)}%</div>
          <div>Mini</div>
        </div>
        <div className="text-left">
          <div><span className="text-orange-400">■</span> {smallChainPct.toFixed(1)}%</div>
          <div>Small</div>
        </div>
        <div className="text-left">
          <div><span className="text-red-400">■</span> {majorChainPct.toFixed(1)}%</div>
          <div>Major</div>
        </div>
      </div>
    </div>
  );
};

export function CityStats({ stats, streets, onClusterClick }: CityStatsProps) {
  const [flippedCard, setFlippedCard] = useState<string | null>(null);

  if (!stats || !streets || streets.length === 0) {
    return (
      <div className="space-y-8">
        <div className="bg-zinc-800 rounded-none p-6">
          <span className="text-zinc-400">No statistics available</span>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Bookshops per 100k",
      value: stats.bookstoresPerCapita || 0,
      icon: BookOpen,
      detail: `${stats.bookstoresPerCapita >= 20 ? 'Very High' : 
               stats.bookstoresPerCapita >= 15 ? 'High' : 
               stats.bookstoresPerCapita >= 10 ? 'Medium' : 
               stats.bookstoresPerCapita >= 5 ? 'Low' : 'Very Low'} Density`,
      secondary: `Rank #${stats.densityRank || 'N/A'} of ${stats.qualityStats?.totalCities || 'N/A'}`,
      valueClass: "text-emerald-400",
      tooltip: `We calculate store density relative to population size. Very High (20+ stores), High (15-19), Medium (10-14), Low (5-9), Very Low (<5) per 100,000 residents.`
    },
    {
      title: "Independent Stores",
      value: stats.independentStats.percentage || 0,
      icon: Store,
      detail: `${stats.independentStats.per100k.toFixed(1) || '0.0'} per 100k residents`,
      secondary: `Rank #${stats.independentStats.rankByPercentage || 'N/A'} of ${stats.qualityStats.totalCities || 'N/A'}`,
      valueClass: "text-blue-400",
      suffix: "%",
      tooltip: `A store is considered independent if its web domain appears only once across cities. Small chains (3-9 locations) and major chains (10+ locations) share the same domain.`
    },
    {
      title: "Store Walkability",
      value: stats.geoStats.walkablePercentage || 0,
      icon: Footprints,
      detail: `${stats.geoStats.walkablePercentage?.toFixed(1) || '0.0'}% within 1km`,
      secondary: `Rank #${stats.geoStats.proximityRank || 'N/A'} of ${stats.geoStats.totalCities || 'N/A'}`,
      valueClass: "text-purple-400",
      suffix: "%",
      tooltip: `For every unique pair of bookstores (e.g. A↔B, A↔C, B↔C), we measure the walking distance. ${stats.geoStats.walkablePercentage?.toFixed(1) || '0.0'}% of these pairs are within 1km of each other.`
    },
    {
      title: "Market Diversity",
      value: stats.marketStats.marketCharacter || 'No data',
      icon: PieChart,
      chart: (
        <MarketDiversityBar 
          independentPct={stats.independentStats.percentage || 0}
          miniChainPct={stats.marketStats.miniChainPct || 0}
          smallChainPct={stats.marketStats.smallChainPct || 0}
          majorChainPct={stats.marketStats.majorChainPct || 0}
        />
      ),
      valueClass: "text-yellow-400",
      tooltip: `Shows the distribution of bookstore types: Major Chains (10+ locations), Small Chains (3-9 locations), and Mini Chains (2 locations).`
    }
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div 
            key={card.title}
            className="h-[195px] perspective-1000"
          >
            <div 
              className={`relative w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
                flippedCard === card.title ? 'rotate-y-180' : ''
              }`}
            >
              {/* Front of card */}
              <div 
                onClick={() => {
                  if (card.tooltip) {
                    setFlippedCard(card.title);
                  }
                }}
                className="absolute w-full h-full bg-zinc-800 rounded-none p-6 hover:ring-1 hover:ring-zinc-700 
                          transition-all duration-300 cursor-pointer backface-hidden"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm text-zinc-400 font-mono">{card.title}</h3>
                  <card.icon className="w-5 h-5 text-zinc-500" />
                </div>
                <div className={`space-y-1 ${card.title === "Market Diversity" ? 'mt-2' : ''}`}>
                  {typeof card.value === 'string' ? (
                    <div className="text-2xl leading-tight mb-2 font-mono">
                      {card.value.split(' ').map((word, i) => (
                        <div key={i} className="leading-tight">{word}</div>
                      ))}
                    </div>
                  ) : (
                    <div className={`text-3xl font-mono ${card.valueClass}`}>
                      {card.value.toFixed(1)}
                      {card.suffix && <span className="text-xl ml-1">{card.suffix}</span>}
                    </div>
                  )}
                  {card.chart && <div>{card.chart}</div>}
                  {card.detail && (
                    <div className="text-xs text-zinc-500 font-mono">{card.detail}</div>
                  )}
                  {card.secondary && (
                    <div className="text-xs text-zinc-500 font-mono">{card.secondary}</div>
                  )}
                </div>
              </div>

              {/* Back of card */}
              {card.tooltip && (
                <div 
                  onClick={() => setFlippedCard(null)}
                  className="absolute w-full h-full bg-zinc-800 rounded-none p-4 cursor-pointer 
                            backface-hidden rotate-y-180 hover:ring-1 hover:ring-zinc-700 overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-2">
                    <RotateCcw className="w-4 h-4 text-zinc-500" />
                  </div>
                  <div className="overflow-y-auto h-[132px] text-xs leading-relaxed text-zinc-300 pr-2">
                    {card.tooltip}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className="h-[195px] perspective-1000"
          onClick={() => setFlippedCard(flippedCard === 'quality' ? null : 'quality')}
        >
          <div 
            className={`relative w-full h-full transition-transform duration-500 transform-style-preserve-3d ${
              flippedCard === 'quality' ? 'rotate-y-180' : ''
            }`}
          >
            {/* Front of Quality Insights card */}
            <div className="absolute w-full h-full bg-zinc-800 rounded-none p-6 hover:ring-1 hover:ring-zinc-700 
                        transition-all duration-300 cursor-pointer backface-hidden">
              <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Quality Insights
              </h3>
              <div className="space-y-2">
                {stats.qualityStats ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Highly Rated:</span>
                      <span className="text-zinc-200 font-mono">
                        {stats.qualityStats.highRatedCount || 0} ({stats.qualityStats.pctHighlyRated?.toFixed(1) || '0.0'}%)
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Top Stores Rating:</span>
                      <span className="text-zinc-200 font-mono">
                        {stats.qualityStats.avgRatingTopStores?.toFixed(1) || '0.0'} ★
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Quality Rank:</span>
                      <span className="text-zinc-200 font-mono">
                        #{stats.qualityStats.rankByQuality || 'N/A'} of {stats.qualityStats.totalCities || 'N/A'} cities
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-zinc-400 text-sm">Quality data not available</div>
                )}
              </div>
            </div>

            {/* Back of Quality Insights card */}
            <div 
              className="absolute w-full h-full bg-zinc-800 rounded-none p-4 cursor-pointer 
                      backface-hidden rotate-y-180 hover:ring-1 hover:ring-zinc-700 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-2">
                <RotateCcw className="w-4 h-4 text-zinc-500" />
              </div>
              <div className="overflow-y-auto h-[195px] text-xs leading-relaxed text-zinc-300 pr-2">                
                <div className="space-y-2">
                  <p>• Highly Rated: Bookstores with 4.5+ stars and 25+ reviews.</p>

                  <p>• Quality Score: Rating × ln(Review Count), normalized to range 0-1.</p>

                  <p>• Ranking: Cities ranked by their concentration of quality bookstores.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-none p-6">
          <h3 className="text-sm text-zinc-400 font-mono mb-4 flex items-center gap-2">
            <Ruler className="w-4 h-4" />
            Major Literary Cluster
          </h3>
          <div className="space-y-2">
            {stats.bestCluster ? (
              <>
                <div 
                  className="text-lg text-zinc-200 font-mono cursor-pointer hover:text-orange-300 transition-colors"
                  onClick={() => stats.bestCluster && onClusterClick?.(stats.bestCluster.location)}
                  title="Click to center map on this cluster"
                >
                  {stats.bestCluster.location}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Nearby Stores:</span>
                  <span className="text-zinc-200 font-mono">{stats.bestCluster.nearbyStores || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Average Rating:</span>
                  <span className="text-zinc-200 font-mono">
                    {stats.bestCluster.avgRating?.toFixed(1) || '0.0'} ★
                  </span>
                </div>
              </>
            ) : (
              <div className="text-zinc-400">No cluster information available</div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
}